import {
  Box,
  Step,
  StepConnector as MuiStepConnector,
  stepConnectorClasses,
  StepIconProps,
  StepLabel as MuiStepLabel,
  stepLabelClasses,
  Stepper,
  styled,
  Theme,
  ThemeOptions,
} from '@mui/material'
import { FC, PropsWithChildren, useMemo } from 'react'
import { Icons, themes } from 'utoniq-ui'

import { useTranslation } from '@/core'

type StatusProps = {
  activeStep: number
}

const { artistTheme } = themes

export const Status = ({ activeStep }: StatusProps) => {
  const { t } = useTranslation('deliveryManagement')
  const steps = useMemo<string[]>(
    () => [t('preparingToShip'), t('deliveryCompleted')],
    [t]
  )
  return (
    <Box sx={{ width: '100%', pb: 5 }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<StepConnector />}
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel StepIconComponent={StepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  )
}

const StepIcon = ({ active, completed, className }: StepIconProps) => {
  return (
    <StepIconRoot ownerState={{ active, completed }} className={className}>
      {active ? (
        <Icons.Selected className="stepIcon-completedIcon" fontSize="small" />
      ) : (
        <div className="stepIcon-circle" />
      )}
    </StepIconRoot>
  )
}

const StepConnector = styled(MuiStepConnector)(({ theme }) => {
  const currentTheme = getThemeByMode(theme)
  return {
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 17,
      left: 'calc(-50% + 5px)',
      right: 'calc(50% + 5px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`&& .${stepConnectorClasses.line}`]: {
        borderColor: currentTheme?.palette?.grey?.['900'],
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`&& .${stepConnectorClasses.line}`]: {
        borderColor: currentTheme?.palette?.grey?.['900'],
      },
    },
    [`&& .${stepConnectorClasses.line}`]: {
      borderColor: currentTheme?.palette?.grey?.['100'],
      borderTopWidth: 2,
      borderRadius: 1,
    },
  }
})

const StepLabel = styled(MuiStepLabel)(({ theme }) => {
  const currentTheme = getThemeByMode(theme)
  return {
    [`&& .${stepLabelClasses.alternativeLabel}`]: {
      marginTop: 8,
      color: currentTheme?.palette?.text?.disabled,
    },
    [`&& .${stepLabelClasses.labelContainer} .${stepLabelClasses.completed}`]: {
      color: currentTheme?.palette?.text?.disabled,
    },
    [`&& .${stepLabelClasses.active}`]: {
      color: currentTheme?.palette?.text?.primary,
    },
  }
})

const StepIconRoot = styled('div')<{
  ownerState: { active?: boolean; completed?: boolean }
}>(({ ownerState, theme }) => {
  const currentTheme = getThemeByMode(theme)
  return {
    color: currentTheme?.palette?.grey?.['100'],
    display: 'flex',
    height: 20,
    alignItems: 'center',
    ...(ownerState.active && {
      color: currentTheme?.palette?.grey?.['900'],
    }),
    '&& .stepIcon-completedIcon': {
      color: currentTheme?.palette?.grey?.['900'],
      zIndex: 1,
    },
    '&& .stepIcon-circle': {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: ownerState.completed
        ? currentTheme?.palette?.grey?.['900']
        : currentTheme?.palette?.grey?.['100'],
    },
  }
}) as unknown as FC<
  PropsWithChildren<{
    ownerState: { active?: boolean; completed?: boolean }
    className?: string
  }>
>

/**
 * @description if mode is 'dark' return defaultTheme otherwise return artistTheme
 * @param theme
 */
function getThemeByMode(theme?: Theme): Theme | ThemeOptions {
  return theme?.palette?.mode === 'dark' ? theme : artistTheme
}
